import { useEffect, useState, type ElementType, type ReactElement } from 'react';
import {
  resolveGenericStatusCardIcon,
  resolveStackedStatusCardIconColor,
} from '@/features/shared/components/chartPanels/StackedMetricsStatusChartSection.icons';
import { StatusMetricCardsGrid } from '@/features/shared/components/chartPanels/StatusMetricCardsGrid';
import { type MetricCardItem } from './statusMetricCards';

type StatusMetricCardsRowProps = {
  cards: MetricCardItem[];
  selectedIds?: string[];
  onToggle?: (id: string) => void;
  loading?: boolean;
  skeletonCount?: number;
  comparisonWindowLabel?: string | null;
  resolveStatusCardIcon?: (statusId: string) => ElementType;
  resolveStatusCardIconColor?: (statusTone: MetricCardItem['statusTone']) => string;
};

type StatusMetricCardsRowSkeletonProps = {
  count?: number;
  comparisonWindowLabel?: string | null;
  resolveStatusCardIcon?: (statusId: string) => ElementType;
  resolveStatusCardIconColor?: (statusTone: MetricCardItem['statusTone']) => string;
};

const StatusMetricCardsRowSkeleton = ({
  count = 5,
  comparisonWindowLabel = null,
  resolveStatusCardIcon = resolveGenericStatusCardIcon,
  resolveStatusCardIconColor = resolveStackedStatusCardIconColor,
}: StatusMetricCardsRowSkeletonProps): ReactElement => (
  <StatusMetricCardsGrid
    metricCards={[]}
    loading
    skeletonCount={count}
    comparisonWindowLabel={comparisonWindowLabel}
    showNoChangePlaceholder={false}
    resolveStatusCardIcon={resolveStatusCardIcon}
    resolveStatusCardIconColor={resolveStatusCardIconColor}
  />
);

const isPlaceholderCard = (card: MetricCardItem): boolean =>
  card.count === '-' && (card.percentage === '-' || card.percentage === undefined);

export const StatusMetricCardsRow = ({
  cards,
  selectedIds,
  onToggle,
  loading,
  skeletonCount,
  comparisonWindowLabel = null,
  resolveStatusCardIcon = resolveGenericStatusCardIcon,
  resolveStatusCardIconColor = resolveStackedStatusCardIconColor,
}: StatusMetricCardsRowProps): ReactElement => {
  const inferredPlaceholderCards = cards.length > 0 && cards.every(isPlaceholderCard);
  const shouldShowSkeleton = Boolean(loading) && cards.length === 0;
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    if (!shouldShowSkeleton && !hasAnimated) {
      setHasAnimated(true);
    }
  }, [hasAnimated, shouldShowSkeleton]);

  if (shouldShowSkeleton) {
    const resolvedCount = skeletonCount ?? 5;
    return (
      <StatusMetricCardsRowSkeleton
        count={resolvedCount}
        comparisonWindowLabel={comparisonWindowLabel}
        resolveStatusCardIcon={resolveStatusCardIcon}
        resolveStatusCardIconColor={resolveStatusCardIconColor}
      />
    );
  }
  return (
    <div className={hasAnimated ? undefined : 'flow-card-enter'}>
      <StatusMetricCardsGrid
        metricCards={cards}
        loading={false}
        skeletonCount={skeletonCount}
        comparisonWindowLabel={comparisonWindowLabel}
        showNoChangePlaceholder={false}
        selectedIds={selectedIds}
        onCardToggle={inferredPlaceholderCards ? undefined : onToggle}
        resolveStatusCardIcon={resolveStatusCardIcon}
        resolveStatusCardIconColor={resolveStatusCardIconColor}
      />
    </div>
  );
};
